import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import ReCAPTCHA from 'react-google-recaptcha';
import './Login.css';
import allSafeLogo from '../../../assets/cloudsafe-logo.png';
import { FaEye, FaEyeSlash, FaLock, FaUser, FaExclamationCircle, FaShieldAlt, FaCheckCircle } from 'react-icons/fa';
import { setToken } from '../../../utils/auth';
import { getDashboardRoute } from '../../../utils/roleUtils';
import { getApiEndpoint } from '../../../config/api';

/** Only same-site paths are honoured as a post-login destination (no open redirects). */
const safeNext = (raw) => {
  const v = String(raw || '');
  return v.startsWith('/') && !v.startsWith('//') && !v.startsWith('/\\') && !v.startsWith('/login') ? v : '';
};

/** Pending team invitation for this user, if any (never blocks sign-in). */
async function firstPendingInvitation(token) {
  try {
    const res = await fetch(getApiEndpoint('/api/contractor/staff/invitations/mine'), {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return null;
    const data = await res.json();
    return Array.isArray(data?.invitations) && data.invitations.length ? data.invitations[0] : null;
  } catch {
    return null;
  }
}

export default function Login() {
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [twoFactorChallenge, setTwoFactorChallenge] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loginAttempts, setLoginAttempts] = useState(0);
  const [captchaValue, setCaptchaValue] = useState(null);
  const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
  const [serverError, setServerError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [touched, setTouched] = useState({});
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const nextPath = safeNext(searchParams.get('next'));
  const justVerified = searchParams.get('verified') === '1';
  const verifyFailed = searchParams.get('verifyFailed') === '1';
  // Set when the server refuses sign-in because the email isn't verified yet.
  const [unverifiedEmail, setUnverifiedEmail] = useState('');
  const [resendState, setResendState] = useState('idle'); // idle | sending | sent | failed

  const completeLogin = async (data) => {
    localStorage.removeItem('userRole');
    localStorage.removeItem('recentProperties');
    localStorage.removeItem('isCompanyAdmin');
    localStorage.removeItem('accountType');
    localStorage.removeItem('internalStaffAccount');

    setToken(data.token);
    const roleForStorage = data.user.internalStaffAccount
      ? 'cloud_staff'
      : (data.user.accountType === 'building_manager' ? 'building_manager' : data.user.role);
    localStorage.setItem('userRole', roleForStorage);
    if (data.user.internalStaffAccount) localStorage.setItem('internalStaffAccount', 'true');
    if (data.user.isCompanyAdmin) localStorage.setItem('isCompanyAdmin', 'true');
    if (data.user.accountType) localStorage.setItem('accountType', data.user.accountType);
    toast.success('Signed in');

    if (nextPath) { navigate(nextPath); return; }
    // Someone invited this person to their team: take them straight to it.
    const invite = await firstPendingInvitation(data.token);
    if (invite?.token) { navigate(`/contractor/invitations/${encodeURIComponent(invite.token)}`); return; }
    navigate(getDashboardRoute(roleForStorage));
  };

  const resendVerification = async () => {
    setResendState('sending');
    try {
      const res = await fetch(getApiEndpoint('/api/auth/resend-verification'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: unverifiedEmail }),
      });
      setResendState(res.ok ? 'sent' : 'failed');
    } catch {
      setResendState('failed');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear field error on edit
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({ ...prev, [name]: '' }));
    }
    if (serverError) setServerError('');
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));
    validateField(name);
  };

  const validateField = (name) => {
    const value = formData[name];
    let error = '';
    if (name === 'email') {
      if (!value.trim()) {
        error = 'Email is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
        error = 'Please enter a valid email address';
      }
    }
    if (name === 'password') {
      if (!value) {
        error = 'Password is required';
      }
    }
    setFieldErrors(prev => ({ ...prev, [name]: error }));
    return error;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setServerError('');
    setUnverifiedEmail('');
    setResendState('idle');

    // Validate all fields
    const emailErr = validateField('email');
    const passErr = validateField('password');
    setTouched({ email: true, password: true });

    if (emailErr || passErr) return;

    // Check captcha after 3 failed attempts
    if (loginAttempts >= 3 && !captchaValue) {
      setServerError('Please complete the captcha verification');
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(getApiEndpoint('/api/auth/login'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password,
          captchaToken: captchaValue
        })
      });

      let data = {};
      try { data = await response.json(); } catch { data = {}; }
      if (response.ok && data.requires2FA && data.challengeToken) {
        setTwoFactorChallenge(data.challengeToken);
        setTwoFactorCode('');
        setServerError('');
      } else if (response.ok && data.token) {
        await completeLogin(data);
      } else if (response.status === 403 && data.emailUnverified) {
        setUnverifiedEmail(formData.email.trim().toLowerCase());
        setServerError(data.message || 'Verify your email address before signing in.');
      } else if (response.status === 403 && data.suspended) {
        setServerError(data.message || 'Your account is suspended. Contact your company admin.');
      } else if (response.status === 403 && data.recoverable) {
        setServerError(data.message);
      } else if (response.status === 429) {
        setServerError(data.message || 'Too many sign-in attempts. Wait a few minutes and try again.');
      } else {
        setLoginAttempts(prev => prev + 1);
        setServerError(data.message || 'Invalid email or password');
      }
    } catch (error) {
      console.error('Login error:', error);
      setServerError('Unable to connect to the server. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTwoFactorSubmit = async (e) => {
    e.preventDefault();
    setServerError('');

    if (!twoFactorCode.trim()) {
      setServerError('Enter your verification code');
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(getApiEndpoint('/api/auth/2fa/login'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          challengeToken: twoFactorChallenge,
          code: twoFactorCode
        })
      });

      const data = await response.json();
      if (response.ok && data.token) {
        await completeLogin(data);
      } else {
        setServerError(data.message || 'Invalid verification code');
      }
    } catch (error) {
      console.error('2FA login error:', error);
      setServerError('Unable to verify your code. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const resetTwoFactorStep = () => {
    setTwoFactorChallenge(null);
    setTwoFactorCode('');
    setServerError('');
  };

  return (
    <div className="login-page">
      <header className="login-header">
        <img src={allSafeLogo} alt="CloudSafe" className="login-header-logo" />
        <div className="login-header-right">
          <span className="login-header-text">New to CloudSafe?</span>
          <Link to="/register" className="login-header-link">Create an account</Link>
        </div>
      </header>

      <div className="login-body">
        <div className="login-card">
          <h1 className="login-title">{twoFactorChallenge ? 'Verification code' : 'Log in'}</h1>
          <p className="login-subtitle">
            {twoFactorChallenge
              ? 'Enter the 6-digit code from your authenticator app, or use a backup code.'
              : 'Welcome back! Please enter your details.'}
          </p>

          {justVerified && !serverError && (
            <div
              className="login-error-alert"
              role="status"
              style={{ background: 'var(--cs-green-light, #f0fdf4)', borderColor: 'var(--cs-green-border, #bbf7d0)', color: 'var(--cs-green-dark, #15803d)' }}
            >
              <FaCheckCircle className="login-error-alert-icon" aria-hidden="true" />
              <span>Email verified — sign in to continue.</span>
            </div>
          )}
          {verifyFailed && !serverError && (
            <div className="login-error-alert" role="alert">
              <FaExclamationCircle className="login-error-alert-icon" aria-hidden="true" />
              <span>That verification link is invalid or has expired. Sign in below and we'll offer to send a new one.</span>
            </div>
          )}

          {serverError && (
            <div className="login-error-alert" role="alert">
              <FaExclamationCircle className="login-error-alert-icon" aria-hidden="true" />
              <span>{serverError}</span>
            </div>
          )}

          {unverifiedEmail && (
            <div style={{ marginBottom: 16 }}>
              <button type="button" className="login-secondary" onClick={resendVerification} disabled={resendState === 'sending'}>
                {resendState === 'sending' ? 'Sending…' : 'Resend verification email'}
              </button>
              <p role="status" aria-live="polite" style={{ margin: '8px 0 0', fontSize: 'var(--cs-text-sm)', color: resendState === 'failed' ? 'var(--cs-red-dark, #b91c1c)' : 'var(--cs-text-secondary, #374151)' }}>
                {resendState === 'sent' && `If ${unverifiedEmail} still needs verifying, a new link is on its way. Check your spam folder too.`}
                {resendState === 'failed' && "We couldn't send a new link — check your connection and try again."}
              </p>
            </div>
          )}

          {twoFactorChallenge ? (
            <form className="login-form" onSubmit={handleTwoFactorSubmit} noValidate>
              <div className="login-field">
                <label className="login-label" htmlFor="login-2fa">Verification code</label>
                <div className="login-input-wrap">
                  <FaShieldAlt className="login-input-icon" />
                  <input
                    id="login-2fa"
                    type="text"
                    className="login-input"
                    placeholder="123456"
                    value={twoFactorCode}
                    onChange={(e) => {
                      setTwoFactorCode(e.target.value);
                      if (serverError) setServerError('');
                    }}
                    autoComplete="one-time-code"
                    inputMode="text"
                    autoFocus
                    required
                  />
                </div>
              </div>

              <button type="submit" className="login-submit" disabled={isLoading}>
                {isLoading && <span className="login-spinner" />}
                {isLoading ? 'Verifying...' : 'Verify and sign in'}
              </button>
              <button type="button" className="login-secondary" onClick={resetTwoFactorStep} disabled={isLoading}>
                Back to password
              </button>
            </form>
          ) : (
          <form className="login-form" onSubmit={handleSubmit} noValidate>
            {/* Email field */}
            <div className="login-field">
              <label className="login-label" htmlFor="login-email">Email</label>
              <div className="login-input-wrap">
                <FaUser className="login-input-icon" />
                <input
                  id="login-email"
                  type="email"
                  name="email"
                  className={`login-input${touched.email && fieldErrors.email ? ' has-error' : ''}`}
                  placeholder="Enter your email"
                  value={formData.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  autoComplete="email"
                  inputMode="email"
                  required
                />
              </div>
              {touched.email && fieldErrors.email && (
                <p className="login-field-error" role="alert">{fieldErrors.email}</p>
              )}
            </div>

            {/* Password field */}
            <div className="login-field">
              <label className="login-label" htmlFor="login-password">Password</label>
              <div className="login-input-wrap">
                <FaLock className="login-input-icon" />
                <input
                  id="login-password"
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  className={`login-input${touched.password && fieldErrors.password ? ' has-error' : ''}`}
                  placeholder="Enter your password"
                  value={formData.password}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  autoComplete="current-password"
                  required
                />
                <button
                  type="button"
                  className="login-pw-toggle"
                  onClick={() => setShowPassword(v => !v)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <FaEyeSlash size={16} /> : <FaEye size={16} />}
                </button>
              </div>
              {touched.password && fieldErrors.password && (
                <p className="login-field-error" role="alert">{fieldErrors.password}</p>
              )}
            </div>

            {/* Forgot */}
            <div className="login-options" style={{ justifyContent: 'flex-end' }}>
              <Link to="/forgot-password" className="login-forgot">Forgot password?</Link>
            </div>

            {/* reCAPTCHA after 3 failed attempts */}
            {loginAttempts >= 3 && (
              <div className="login-captcha">
                {!recaptchaSiteKey ? (
                  <p className="login-captcha-missing" role="alert">
                    Verification could not load (missing site key). Rebuild the app with{' '}
                    <code>REACT_APP_RECAPTCHA_SITE_KEY</code> set, or contact support.
                  </p>
                ) : (
                  <ReCAPTCHA
                    sitekey={recaptchaSiteKey}
                    onChange={(value) => setCaptchaValue(value)}
                    onErrored={() =>
                      setServerError(
                        'Could not load verification. Allow scripts from Google in your browser, or try disabling extensions.'
                      )
                    }
                  />
                )}
              </div>
            )}

            {/* Submit */}
            <button type="submit" className="login-submit" disabled={isLoading}>
              {isLoading && <span className="login-spinner" />}
              {isLoading ? 'Signing in...' : 'Sign in'}
            </button>
          </form>
          )}

          <p className="login-terms">
            By signing in, you agree to our{' '}
            <Link to="/terms">Terms of Service</Link> and{' '}
            <Link to="/privacy">Privacy Policy</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
